import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, isClickedOutside } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  /**
   * Clears the cached product HTML to force a fresh fetch on next open
   */
  clearCache() {
    this.#cachedProductHtml = null;
    // Abort any pending fetch
    this.#abortController?.abort();
    this.#abortController = null;
  }

  get productPageUrl() {
    // Prefer product-card link if present
    const productCardLink = /** @type {HTMLAnchorElement} */ (
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')
    );

    if (productCardLink?.href) return productCardLink.href;

    // Fallback: explicit data attribute on the component
    const dataUrl = this.dataset.productUrl;
    if (dataUrl) return dataUrl;

    // Only fallback to an internal anchor when explicitly targeting the cart recommendations modal
    if (this.dataset.dialogId || this.closest('.cart-recommendations-slider')) {
      const internalAnchor = /** @type {HTMLAnchorElement} */ (this.querySelector('a[href]'));
      if (internalAnchor?.href) return internalAnchor.href;
    }

    return '';
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentProductUrl = this.productPageUrl;
    
    // Check if we need to fetch or re-fetch the product page
    // Clear cache if the URL has changed (e.g., product changed via combine listing)
    if (this.#cachedProductHtml) {
      // Try to detect if the cached HTML is for a different product
      // by checking if the product URL in the cached HTML matches current URL
      const cachedProductLink = this.#cachedProductHtml.querySelector('product-card a[ref="productCardLink"]');
      const cachedUrl = cachedProductLink?.getAttribute('href')?.split('?')[0];
      const currentUrl = currentProductUrl?.split('?')[0];
      
      if (cachedUrl && currentUrl && cachedUrl !== currentUrl) {
        // Product has changed, clear cache
        this.clearCache();
      }
    }

    if (!this.#cachedProductHtml && currentProductUrl) {
      await this.fetchProductPage(currentProductUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      const targetDialogId = this.dataset.dialogId || 'quick-add-dialog';
      await this.updateQuickAddModal(freshHtmlCopy, targetDialogId);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogId = this.dataset.dialogId || 'quick-add-dialog';
    const dialogComponent = document.getElementById(dialogId);
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    // Pass context for scoping behavior/styles
    if (this.dataset.quickAddContext) {
      dialogComponent.setAttribute('data-context', this.dataset.quickAddContext);
      dialogComponent.addEventListener(
        DialogCloseEvent.eventName,
        () => dialogComponent.removeAttribute('data-context'),
        { once: true }
      );
    }

    // Pass source attribution (e.g. nosto) for analytics/scoped behavior.
    if (this.dataset.source) {
      dialogComponent.setAttribute('data-source', this.dataset.source);
      dialogComponent.addEventListener(
        DialogCloseEvent.eventName,
        () => dialogComponent.removeAttribute('data-source'),
        { once: true }
      );
    } else {
      dialogComponent.removeAttribute('data-source');
    }

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the quick add modal content.
   * @param {Document} newHtml - The new HTML.
   * @param {string} targetDialogId - The dialog id to scope content into.
   */
  async updateQuickAddModal(newHtml, targetDialogId) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    /** @type {HTMLElement | null} */
    const modalContent = targetDialogId
      ? document.querySelector(`#${CSS.escape(targetDialogId)} #quick-add-modal-content`)
      : document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Extract product data from the new HTML before morphing
    const newProductCard = productGrid.querySelector('product-card');
    const newProductId = newProductCard?.getAttribute('data-product-id');
    const newProductForm = productGrid.querySelector('product-form-component');
    const newVariantPicker = productGrid.querySelector('variant-picker');
    const newProductUrl = newVariantPicker?.getAttribute('data-product-url') || 
                          newProductCard?.querySelector('a[ref="productCardLink"]')?.getAttribute('href')?.split('?')[0] ||
                          this.productPageUrl?.split('?')[0];

    // Replace the full media gallery with the featured image only
    try {
      const mediaSection = productGrid.querySelector('.product-information__media');
      if (mediaSection) {
        /** @type {HTMLImageElement | null} */
        let firstImage = mediaSection.querySelector('img');
        // If the image is inside a <picture>, grab that img
        if (!firstImage) firstImage = mediaSection.querySelector('picture img');

        if (firstImage) {
          const simpleMedia = document.createElement('div');
          simpleMedia.className = 'product-information__media';

          const imgClone = /** @type {HTMLImageElement} */ (firstImage.cloneNode(true));
          imgClone.removeAttribute('sizes');
          imgClone.loading = 'eager';

          const wrapper = document.createElement('div');
          wrapper.className = 'product-media';
          wrapper.appendChild(imgClone);

          simpleMedia.appendChild(wrapper);
          mediaSection.replaceWith(simpleMedia);
        }
      }
    } catch (err) {
      console.warn('QuickAdd: Failed to simplify media gallery', err);
    }

    // Ensure product title exists for both mobile and desktop
    const productDetails = productGrid.querySelector('.product-details');
    const existingTitle = productDetails?.querySelector('.view-product-title');
    
    if (!existingTitle && productDetails && this.dataset.productTitle) {
      // Create view-product-title if it doesn't exist
      const viewProductTitle = document.createElement('div');
      viewProductTitle.className = 'view-product-title';
      const titleLink = document.createElement('a');
      titleLink.href = this.productPageUrl;
      titleLink.textContent = this.dataset.productTitle;
      viewProductTitle.appendChild(titleLink);
      
      // Insert at the beginning of product-details > group-block > group-block-content
      const groupBlockContent = productDetails.querySelector('.group-block-content');
      if (groupBlockContent) {
        groupBlockContent.insertBefore(viewProductTitle, groupBlockContent.firstChild);
      }
    }

    morph(modalContent, productGrid);

    // Move variant selector to desired position
    requestAnimationFrame(() => {
      const modal = document.querySelector('.quick-add-modal');
      const variantSelector = modalContent.querySelector('variant-picker');

      console.log(modalContent.querySelector('.product-details'));
      
      if (variantSelector && modal) {
        // Remove any existing selectors to prevent stacking
        modal.querySelectorAll('variant-picker').forEach((el) => el.remove());
        
        modal.appendChild(variantSelector);
      }
    });

    // After morphing, ensure variant picker and product form have correct product data
    // This is critical for products with multiple options (size, inseam, liner, etc.)
    // The variant picker needs the correct product URL to fetch variant data when options are selected
    // Use requestAnimationFrame to ensure DOM is updated after morph
    requestAnimationFrame(() => {
      const modalVariantPicker = modalContent.querySelector('variant-picker');
      const modalProductForm = modalContent.querySelector('product-form-component');
      
      // Pass cart edit data to product form if in cart-edit context
      // This needs to happen regardless of variant picker existence
      const isCartEdit = this.dataset.quickAddContext === 'cart-edit';
      const cartLineKey = this.dataset.cartLineKey;
      const selectedVariantId = this.dataset.selectedVariantId;
      
      const setCartEditData = () => {
        const productForm = modalContent.querySelector('product-form-component');
        if (isCartEdit && productForm && cartLineKey && selectedVariantId) {
          productForm.setAttribute('data-cart-edit', 'true');
          productForm.setAttribute('data-cart-line-key', cartLineKey);
          productForm.setAttribute('data-cart-old-variant-id', selectedVariantId);
        }
      };
      
      // Try immediately and again after a delay to ensure product form is ready
      setCartEditData();
      setTimeout(setCartEditData, 100);

      // Pass recommendation source to product form for downstream analytics.
      if (modalProductForm) {
        if (this.dataset.source) {
          modalProductForm.setAttribute('data-source', this.dataset.source);
        } else {
          modalProductForm.removeAttribute('data-source');
        }
      }
      
      if (modalVariantPicker && modalProductForm) {
        // Update product form component with new product ID FIRST
        // This ensures VariantUpdateEvent handlers don't ignore updates
        if (newProductId) {
          modalProductForm.setAttribute('data-product-id', String(newProductId));
        }
        
        if (newProductId && newProductUrl) {
          // Update variant picker with new product data
          // This ensures that when options are selected, the variant picker fetches
          // variant data from the correct product URL
          modalVariantPicker.setAttribute('data-product-id', String(newProductId));
          modalVariantPicker.setAttribute('data-product-url', String(newProductUrl));
          
          // CRITICAL: Ensure the form's variant ID input is initialized with the current variant from JSON
          // This must happen immediately after morphing to prevent submitting with old variant ID
          const jsonEl = modalVariantPicker.querySelector('script[type="application/json"]');
          if (jsonEl) {
            try {
              const variantData = JSON.parse(jsonEl.textContent);
              if (variantData && variantData.id) {
                const form = modalProductForm.querySelector('form');
                const variantInput = form?.querySelector('input[name="id"]');
                if (variantInput instanceof HTMLInputElement) {
                  variantInput.value = String(variantData.id);
                  variantInput.removeAttribute('disabled');
                  // Also update the refs if available (for ProductFormComponent)
                  // @ts-ignore
                  if (modalProductForm.refs && modalProductForm.refs.variantId) {
                    // @ts-ignore
                    modalProductForm.refs.variantId.value = String(variantData.id);
                  }
                  // Mark that the form has been initialized with the new product
                  if (form) {
                    form.setAttribute('data-variant-initialized', 'true');
                  }
                }
              }
            } catch (e) {
              console.warn('QuickAdd: Failed to parse variant JSON', e);
            }
          }
        }
      }

      // Update button text to "Update Cart" when in cart-edit context
      // This runs regardless of whether variant picker/product form exist
      // Note: Check component's context since dialog context is set later in #openQuickAddModal
      if (isCartEdit) {
        // Try multiple times to ensure we catch the button when it's ready
        const updateButtonText = () => {
          const addToCartTexts = modalContent.querySelectorAll('.add-to-cart-text__content');
          addToCartTexts.forEach(textEl => {
            textEl.textContent = 'Update Cart';
          });
        };
        
        // Try immediately, then after a short delay, then after a longer delay
        updateButtonText();
        setTimeout(updateButtonText, 50);
        setTimeout(updateButtonText, 200);
      }
    });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #isDocked = false;
  /** @param {MouseEvent} event */
  #onClickDocked = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };
  /** @param {KeyboardEvent} event */
  #onKeyDownDocked = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Show as non-modal when inside the cart drawer, so it docks within the drawer.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Dock when inside cart drawer (check if parent is cart-drawer-component) or when context is cart-related
    const context = this.getAttribute('data-context');
    const isInsideCartDrawer = this.closest('cart-drawer-component') !== null;
    this.#isDocked = isInsideCartDrawer || context === 'cart-recommendations' || context === 'cart-edit';

    if (this.#isDocked) {
      dialog.show();
      this.dispatchEvent(new DialogOpenEvent());
      setTimeout(() => {
        this.addEventListener('click', this.#onClickDocked);
        this.addEventListener('keydown', this.#onKeyDownDocked);
      });
    } else {
      super.showDialog();
    }
  }

  // Use base closeDialog; remove our docked listeners on close

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, () => {
      if (this.#isDocked) {
        this.removeEventListener('click', this.#onClickDocked);
        this.removeEventListener('keydown', this.#onKeyDownDocked);
      }
      // Ensure variant selector is cleared on close
      this.querySelectorAll('.quick-add-modal variant-picker').forEach((el) => el.remove());
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    
    // Update button text on variant update if in cart-edit context
    const isCartEdit = this.getAttribute('data-context') === 'cart-edit';
    if (isCartEdit) {
      // Use requestAnimationFrame to ensure morph has completed
      requestAnimationFrame(() => {
        setTimeout(() => {
          const modalContent = this.querySelector('#quick-add-modal-content');
          if (modalContent) {
            const addToCartTexts = modalContent.querySelectorAll('.add-to-cart-text__content');
            addToCartTexts.forEach(textEl => {
              textEl.textContent = 'Update Cart';
            });
          }
        }, 50);
      });
    }
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
